if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        
        // Store the total media count from HTML before slider modifies it
        if (this.elements.viewer) {
          try {
            const totalElement = this.elements.viewer.querySelector('.slider-counter--total');
            if (totalElement && totalElement.textContent) {
              this.mediaCount = parseInt(totalElement.textContent, 10);
              if (isNaN(this.mediaCount) || this.mediaCount < 1) {
                this.mediaCount = 1;
              }
            }
            
            // Ensure slider buttons are properly initialized
            // Wait for slider component to be ready
            setTimeout(() => {
              try {
                // Check if buttons exist and are clickable
                const prevButton = this.elements.viewer.querySelector('button[name="previous"]');
                const nextButton = this.elements.viewer.querySelector('button[name="next"]');
                
                if (prevButton && nextButton) {
                  // Ensure buttons are not disabled
                  prevButton.removeAttribute('disabled');
                  nextButton.removeAttribute('disabled');
                  
                  // Override slider's update method to prevent it from resetting our counter
                  if (this.elements.viewer.update && !this.elements.viewer._updateOverridden) {
                    const originalUpdate = this.elements.viewer.update.bind(this.elements.viewer);
                    this.elements.viewer.update = (...args) => {
                      // Get the correct page number based on active slide BEFORE slider calculates it
                      const allSlides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
                      const activeSlide = allSlides.find(slide => slide.classList.contains('is-active'));
                      let correctPage = 1;
                      if (activeSlide) {
                        const activeIndex = allSlides.indexOf(activeSlide);
                        correctPage = activeIndex + 1;
                      }
                      
                      const result = originalUpdate(...args);
                      
                      // Immediately override the counter with correct value
                      if (this.elements.viewer.currentPageElement) {
                        this.elements.viewer.currentPageElement.textContent = correctPage;
                      }
                      if (this.elements.viewer.currentPage !== undefined) {
                        this.elements.viewer.currentPage = correctPage;
                      }
                      
                      // Also update via our method to ensure consistency
                      setTimeout(() => {
                        this.updateCounter(correctPage);
                      }, 10);
                      
                      return result;
                    };
                    this.elements.viewer._updateOverridden = true;
                  }
                  
                  // Add manual click handlers as fallback if slider didn't set up properly
                  if (!prevButton.hasAttribute('data-handler-added')) {
                    prevButton.addEventListener('click', (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.navigateSlider(-1);
                    });
                    prevButton.setAttribute('data-handler-added', 'true');
                  }
                  
                  if (!nextButton.hasAttribute('data-handler-added')) {
                    nextButton.addEventListener('click', (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      this.navigateSlider(1);
                    });
                    nextButton.setAttribute('data-handler-added', 'true');
                  }
                  
                  // Force re-initialization if slider didn't set up properly
                  if (this.elements.viewer.initPages) {
                    // Force recalculation
                    this.elements.viewer.sliderItems = this.elements.viewer.querySelectorAll('[id^="Slide-"]');
                    if (!this.elements.viewer.sliderItemsToShow || this.elements.viewer.sliderItemsToShow.length < 2) {
                      this.elements.viewer.initPages();
                    }
                  }
                  
                  // Initialize counter to show current active slide
                  const allSlides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
                  const activeSlide = allSlides.find(slide => slide.classList.contains('is-active'));
                  if (activeSlide) {
                    const activeIndex = allSlides.indexOf(activeSlide);
                    this.updateCounter(activeIndex + 1);
                  }
                }
                
                this.fixSliderCount();
              } catch (e) {
                console.warn('Error fixing slider count:', e);
              }
            }, 300);
            
            // Fix count on slideChanged events
            this.elements.viewer.addEventListener('slideChanged', () => {
              try {
                this.fixSliderCount();
              } catch (e) {
                // Silently ignore errors
              }
            });
          } catch (e) {
            console.warn('Error initializing media gallery counter:', e);
          }
        }
        
        // Always listen to slideChanged to update is-active class, even without thumbnails
        if (this.elements.viewer) {
          try {
            // Check if debounce is available
            const debouncedHandler = typeof debounce === 'function' 
              ? debounce(this.onSlideChanged.bind(this), 500)
              : this.onSlideChanged.bind(this);
            this.elements.viewer.addEventListener('slideChanged', debouncedHandler);
          } catch (e) {
            console.warn('Error setting up slideChanged listener:', e);
          }
        }
        
        // Only set up thumbnail interactions if thumbnails exist
        if (this.elements.thumbnails) {
          try {
            this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
              const button = mediaToSwitch.querySelector('button');
              if (button) {
                button.addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
              }
            });
          } catch (e) {
            console.warn('Error setting up thumbnail interactions:', e);
          }
        }
        
        if (this.dataset.desktopLayout && this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) {
          this.removeListSemantic();
        }
      }

      fixSliderCount() {
        if (!this.elements.viewer || !this.mediaCount) return;
        
        try {
          // Find the total element and fix it
          const totalElement = this.elements.viewer.querySelector('.slider-counter--total');
          if (totalElement) {
            const currentValue = parseInt(totalElement.textContent, 10);
            if (isNaN(currentValue) || currentValue !== this.mediaCount) {
              totalElement.textContent = this.mediaCount;
            }
          }
          
          // Also update the slider component's internal count if accessible
          const sliderComponent = this.elements.viewer;
          if (sliderComponent && sliderComponent.pageTotalElement) {
            sliderComponent.pageTotalElement.textContent = this.mediaCount;
            if (typeof sliderComponent.totalPages !== 'undefined') {
              sliderComponent.totalPages = this.mediaCount;
            }
          }
        } catch (e) {
          // Silently ignore errors
        }
      }

      navigateSlider(direction) {
        if (!this.elements.viewer) return;
        
        try {
          const allSlides = Array.from(this.elements.viewer.querySelectorAll('[id^="Slide-"]'));
          const currentSlide = allSlides.find(slide => slide.classList.contains('is-active'));
          
          if (!currentSlide && allSlides.length > 0) {
            // If no active slide, make first one active
            allSlides[0].classList.add('is-active');
            this.updateCounter(1);
            return;
          }
          
          if (!currentSlide) return;
          
          const currentIndex = allSlides.indexOf(currentSlide);
          let nextIndex = currentIndex + direction;
          
          // Loop around if at boundaries
          if (nextIndex < 0) {
            nextIndex = allSlides.length - 1;
          } else if (nextIndex >= allSlides.length) {
            nextIndex = 0;
          }
          
          // Remove active class from all
          allSlides.forEach(slide => slide.classList.remove('is-active'));
          
          // Add active class to new slide
          allSlides[nextIndex].classList.add('is-active');
          
          // Update counter immediately and prevent slider from overriding
          this.updateCounter(nextIndex + 1);
          
          // Update slider component's internal state if it exists
          if (this.elements.viewer.currentPage !== undefined) {
            this.elements.viewer.currentPage = nextIndex + 1;
          }
          
          // Trigger slideChanged event for other listeners
          if (this.elements.viewer) {
            this.elements.viewer.dispatchEvent(
              new CustomEvent('slideChanged', {
                detail: {
                  currentPage: nextIndex + 1,
                  currentElement: allSlides[nextIndex],
                },
              })
            );
          }
          
          // Play active media (for videos, etc.)
          this.playActiveMedia(allSlides[nextIndex]);
          
          // Update again after a short delay to ensure it sticks
          setTimeout(() => {
            this.updateCounter(nextIndex + 1);
          }, 50);
        } catch (e) {
          console.warn('Error navigating slider:', e);
        }
      }

      updateCounter(currentPage) {
        if (!this.elements.viewer) return;
        
        try {
          const currentElement = this.elements.viewer.querySelector('.slider-counter--current');
          if (currentElement) {
            currentElement.textContent = currentPage;
          }
          
          // Also update slider component's counter if it exists
          if (this.elements.viewer.currentPageElement) {
            this.elements.viewer.currentPageElement.textContent = currentPage;
          }
          
          // Update slider component's internal currentPage
          if (this.elements.viewer.currentPage !== undefined) {
            this.elements.viewer.currentPage = currentPage;
          }
        } catch (e) {
          // Silently ignore
        }
      }

      onSlideChanged(event) {
        // Update is-active class on the current slide
        if (event.detail && event.detail.currentElement) {
          const currentSlide = event.detail.currentElement;
          const allSlides = this.elements.viewer.querySelectorAll('[data-media-id]');
          
          // Remove is-active from all slides
          allSlides.forEach((slide) => {
            slide.classList.remove('is-active');
          });
          
          // Add is-active to current slide
          if (currentSlide && currentSlide.dataset.mediaId) {
            currentSlide.classList.add('is-active');
          }
        }
        
        // Update thumbnail if it exists
        if (this.elements.thumbnails && event.detail && event.detail.currentElement) {
          const thumbnail = this.elements.thumbnails.querySelector(
            `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
          );
          this.setActiveThumbnail(thumbnail);
        }
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);
        if (this.elements.viewer.slider && this.elements.viewer.resetPages) {
            this.elements.viewer.resetPages();
        }

        if (!this.elements.thumbnails) return;
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
